import { ProjectHelper, HTTPRequestResponse } from '@bimser/common';
import { IDictionary } from '@bimser/core';
import { Plugins } from '@bimser/studio-core';
import { getDataSourceParams } from '.';
import { IGetDataSourceData } from '../entities/common/DataSource/IGetDataSourceData';
import { IDataSource } from '../entities/common';

export async function getDataSourceData({
	dataSource,
	projectName,
	fieldParams,
	reload,
	loadOptions,
	forceRefresh,
	requiredError,
	clientCacheId,
	id,
	projectVersion
}: IGetDataSourceData): Promise<
	HTTPRequestResponse<Plugins.QueryDesigner.Entities.Bases.IDataSourceResponse>
> {
	try {
		if (requiredError) {
			throw new Error('Required error triggered');
		}

		if (!isRequestValid(dataSource, projectName, reload)) {
			throw new Error('Invalid request data');
		}

		const paramArgs = getDataSourceParams(dataSource, fieldParams);

		if (!paramArgs.isValid) {
			throw new Error('Invalid parameters');
		}

		const requestData = buildRequestData(loadOptions, forceRefresh);
		const controller = manageAbortController(id);

		const response = await sendRequest({
			dataSource,
			projectName,
			paramArgs,
			requestData,
			clientCacheId,
			forceRefresh,
			controller,
			projectVersion
		});

		if (!response.data.success) {
			throw new Error('Request failed');
		}

		return response;
	} catch (error) {
		handleError(error, dataSource?.name);
		throw error;
	}
}

function isRequestValid(
	dataSource: IDataSource | undefined,
	projectName: string | undefined,
	reload: boolean
): boolean {
	const hasFieldSelectionParam = dataSource?.parameters.some(
		i => i.valueModifier === 'fieldSelection'
	);
	return (
		dataSource &&
		!dataSource.runAtServer &&
		dataSource.name &&
		projectName &&
		(!hasFieldSelectionParam || reload)
	);
}

function buildRequestData(
	loadOptions: any,
	forceRefresh: boolean
): Plugins.QueryDesigner.Entities.Bases.IBaseDataSourceRequest {
	return {
		loadOptions,
		forceRefresh
	};
}

const controllers: IDictionary<AbortController> = {};

function manageAbortController(id?: string): AbortController | undefined {
	if (!id) return;

	controllers[id]?.abort();

	const controller = new AbortController();

	controllers[id] = controller;

	return controller;
}

async function sendRequest({
	dataSource,
	projectName,
	paramArgs,
	requestData,
	clientCacheId,
	forceRefresh,
	controller,
	projectVersion
}: {
	dataSource: IDataSource;
	projectName: string;
	paramArgs: any;
	requestData: Plugins.QueryDesigner.Entities.Bases.IBaseDataSourceRequest;
	clientCacheId?: string;
	forceRefresh: boolean;
	controller?: AbortController;
	projectVersion: number;
}): Promise<HTTPRequestResponse<Plugins.QueryDesigner.Entities.Bases.IDataSourceResponse>> {
	return ProjectHelper.RunPromiseFormService({
		projectName,
		formName: 'DataSource',
		serviceName: dataSource.name,
		projectVersion,
		requestParameters: { ...paramArgs.requestPayload, ...requestData },
		requestConfig: {
			headers: { 'Content-Type': 'application/json; charset=utf-8' },
			cache: true,
			useClientCachedData: forceRefresh ? false : dataSource.useClientCachedData,
			clientCacheId,
			controller
		}
	});
}

function handleError(error: any, serviceName?: string) {
	if (error?.message !== 'cancel') {
		console.warn(`Error occurred while reaching ${serviceName} endpoint.`);
	}
}
